import { useCallback, useEffect, useMemo, useState } from "react";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import { useTranslation } from "react-i18next";

import {
  GetDemoRequestsCall,
  UpdateDemoRequestStatusCall,
  DeleteDemoRequestCall,
  SyncCrmInboxCall,
} from "../../redux/api/demoRequest.api";
import CustomerCard from "./CustomerCard";
import { AddCustomerDialog, TemplatesDialog, SendEmailDialog } from "./crmDialogs";

const TIME_LABELS = {
  morning: "Morning (9am–12pm)",
  midday: "Midday (12pm–3pm)",
  afternoon: "Afternoon (3pm–6pm)",
  flexible: "Flexible",
};

const PILOT_LABELS = {
  yes: "Yes, I am interested",
  maybe: "Maybe later",
  not_now: "Not right now",
};

const DEFAULT_STATUS_KEYS = [
  "new_request",
  "contacted",
  "scheduled",
  "completed",
  "no_show",
  "rejected",
  "client",
];

const STATUS_LABELS_EN = {
  new_request: "New Request",
  contacted: "Contacted",
  scheduled: "Scheduled",
  completed: "Completed",
  no_show: "No Show",
  rejected: "Rejected",
  client: "Client",
};

/*
  demo_dates is a JSONB column on the backend (DemoRequest model) and
  the API doesn't enforce a fixed item shape. We've seen at least:
    - "2026-05-30"                            ← original mobile build
    - "2026-05-30T09:00:00.000Z"              ← ISO datetime
    - { date: "2026-05-30" }                  ← newer mobile build
    - { ymd: "2026-05-30", weekday: "Sat" }
    - { year: 2026, month: 5, day: 30 }
    - Date instances serialised to ISO
  Anything else (or null) becomes null and is dropped from the output
  rather than rendering as "[object Object]".
*/
function normalizeDemoDate(item) {
  if (item == null) return null;
  if (item instanceof Date) {
    const iso = item.toISOString();
    return iso.slice(0, 10);
  }
  if (typeof item === "string") {
    const s = item.trim();
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const iso = s.length >= 10 ? s.slice(0, 10) : s;
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : s;
  }
  if (typeof item === "object") {
    const candidates = [
      item.date,
      item.ymd,
      item.day,
      item.value,
      item.iso,
      item.start,
      item.startDate,
    ];
    for (const c of candidates) {
      const norm = normalizeDemoDate(c);
      if (norm) return norm;
    }
    if (
      Number.isFinite(Number(item.year)) &&
      Number.isFinite(Number(item.month)) &&
      Number.isFinite(Number(item.day))
    ) {
      const y = String(item.year).padStart(4, "0");
      const m = String(item.month).padStart(2, "0");
      const d = String(item.day).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return null;
  }
  return String(item);
}

function slotClock(item) {
  if (!item || typeof item !== "object") return "";
  const time = String(item.time || "").trim();
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return "";
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

function formatDates(demoDates) {
  if (!Array.isArray(demoDates) || demoDates.length === 0) return "—";
  const normalised = demoDates
    .map((item) => {
      const ymd = normalizeDemoDate(item);
      if (!ymd) return null;
      const clock = slotClock(item);
      return clock ? `${ymd} · ${clock}` : ymd;
    })
    .filter(Boolean);
  return normalised.length ? normalised.join(", ") : "—";
}

function formatDemoDatesPretty(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return "—";
  const out = arr
    .map((raw) => {
      const ymd = normalizeDemoDate(raw);
      if (!ymd) return null;
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
      if (!match) return ymd;
      const [, ys, ms, ds] = match;
      const dt = new Date(Number(ys), Number(ms) - 1, Number(ds));
      if (Number.isNaN(dt.getTime())) return ymd;
      const clock = slotClock(raw);
      const label = clock ? `${dt.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })} · ${clock}` : dt.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return label;
    })
    .filter(Boolean);
  return out.length ? out.join("; ") : "—";
}

function labelTime(v) {
  if (v == null || v === "") return "—";
  return TIME_LABELS[String(v)] || String(v);
}

/*
  `users.country` is stored as an ISO 3166-1 alpha-2 code (e.g. "IT",
  "IE"). We turn it into a flag emoji using regional-indicator code
  points — no extra package needed, works in every modern browser /
  OS that has flag emojis (everything except Windows native fonts).
  Returns an empty string for missing / malformed input.
*/
function isoToFlag(iso) {
  if (!iso || typeof iso !== "string") return "";
  const code = iso.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(
    base + code.charCodeAt(0) - a,
    base + code.charCodeAt(1) - a
  );
}

/*
  University display name: prefer the *current* linked university on
  the user (universityInfo) so admins always see the latest binding,
  fall back to the snapshot stored on the demo request row (which is
  whatever the user had at submission time).
*/
function universityDisplayName(row) {
  const live = row?.user?.universityInfo?.name;
  const snapshot = row?.university?.name;
  const typed = row?.university_name;
  return live || snapshot || typed || "";
}

function requesterName(row) {
  const user = row?.user;
  const fromForm = `${row?.first_name || ""} ${row?.last_name || ""}`.trim();
  const fromAccount = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return fromForm || fromAccount || "";
}

function requesterCountry(row) {
  return String(row?.country || row?.user?.country || "").trim();
}

function uniqueSorted(values) {
  return Array.from(
    new Set(
      (values || [])
        .map((v) => String(v == null ? "" : v).trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function rowMatchesQuery(row, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    requesterName(row),
    row?.first_name,
    row?.last_name,
    row?.work_email,
    row?.user?.email,
    row?.phone,
    row?.user?.phone,
    universityDisplayName(row),
    requesterCountry(row),
    row?.academic_role,
    row?.account_role,
    row?.teaching_area,
    row?.class_size,
    row?.main_interest,
    row?.main_goal,
    row?.time_preference,
    row?.pilot,
    row?.notes,
    row?.status,
    row?.source,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

const EMPTY_FILTERS = {
  search: "",
  country: "",
  university: "",
  classSize: "",
  status: "",
  academicRole: "",
  teachingArea: "",
  source: "",
  answer: "",
  pilot: "",
};

function labelPilot(v) {
  if (v == null || v === "") return "—";
  return PILOT_LABELS[String(v)] || String(v);
}

function statusChipColor(key) {
  switch (key) {
    case "new_request":
      return "info";
    case "contacted":
      return "primary";
    case "scheduled":
      return "secondary";
    case "completed":
      return "success";
    case "no_show":
      return "warning";
    case "rejected":
      return "error";
    case "client":
      return "default";
    default:
      return "default";
  }
}

function flattenForDisplay(obj, prefix = "") {
  if (obj == null) return [];
  if (typeof obj !== "object") {
    return [{ key: prefix || "value", value: String(obj) }];
  }
  if (Array.isArray(obj)) {
    return [{ key: prefix || "items", value: JSON.stringify(obj) }];
  }
  const out = [];
  Object.keys(obj).forEach((k) => {
    const path = prefix ? `${prefix}.${k}` : k;
    const val = obj[k];
    if (val == null) {
      out.push({ key: path, value: "—" });
    } else if (typeof val === "object" && !Array.isArray(val)) {
      out.push(...flattenForDisplay(val, path));
    } else if (Array.isArray(val)) {
      out.push({ key: path, value: JSON.stringify(val) });
    } else {
      out.push({ key: path, value: String(val) });
    }
  });
  return out;
}

function DetailLine({ label, value }) {
  const display =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <Grid container spacing={1} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {display}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default function DemoRequestsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [statusKeys, setStatusKeys] = useState(DEFAULT_STATUS_KEYS);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [emailRow, setEmailRow] = useState(null);
  const [inboxNote, setInboxNote] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    color: "success",
  });

  const setFilter = (key) => (event) => {
    const value = event?.target ? event.target.value : event;
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { rows: data, statuses } = await GetDemoRequestsCall();
    setRows(Array.isArray(data) ? data : []);
    if (Array.isArray(statuses) && statuses.length > 0) {
      setStatusKeys(statuses);
    }
    setLoading(false);
  }, []);

  const showToast = useCallback((message, color = "success") => {
    setSnackbar({ show: true, message, color });
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const checkReplies = useCallback(async ({ silent } = {}) => {
    const res = await SyncCrmInboxCall();
    if (res?.configured === false && res?.message) setInboxNote(res.message);
    if (res?.newly_replied > 0) {
      await fetchRows();
      showToast(
        res.newly_replied === 1
          ? "1 reply flagged on a customer card."
          : `${res.newly_replied} replies flagged on customer cards.`
      );
    } else if (!silent && res?.configured) {
      showToast("Inbox checked. No new replies.");
    } else if (!silent && res?.message) {
      showToast(res.message, "warning");
    }
  }, [fetchRows, showToast]);

  useEffect(() => {
    checkReplies({ silent: true });
    const timer = setInterval(() => {
      checkReplies({ silent: true });
    }, 60000);
    return () => clearInterval(timer);
  }, [checkReplies]);

  const filterOptions = useMemo(() => {
    return {
      countries: uniqueSorted(rows.map((r) => requesterCountry(r))),
      universities: uniqueSorted(rows.map((r) => universityDisplayName(r))),
      classSizes: uniqueSorted(rows.map((r) => r.class_size)),
      academicRoles: uniqueSorted(rows.map((r) => r.academic_role)),
      teachingAreas: uniqueSorted(rows.map((r) => r.teaching_area)),
      pilots: uniqueSorted(rows.map((r) => r.pilot)),
      sources: uniqueSorted(rows.map((r) => r.source)),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!rowMatchesQuery(row, filters.search)) return false;
      if (filters.country && requesterCountry(row) !== filters.country) return false;
      if (filters.university && universityDisplayName(row) !== filters.university) return false;
      if (filters.classSize && String(row.class_size || "") !== filters.classSize) return false;
      if (filters.status && String(row.status || "new_request") !== filters.status) return false;
      if (filters.academicRole && String(row.academic_role || "") !== filters.academicRole) {
        return false;
      }
      if (filters.teachingArea && String(row.teaching_area || "") !== filters.teachingArea) {
        return false;
      }
      if (filters.source && String(row.source || "") !== filters.source) return false;
      if (filters.pilot && String(row.pilot || "") !== filters.pilot) return false;
      if (filters.answer) {
        const answer = row.email_summary?.answer_status || "not_sent";
        if (answer !== filters.answer) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).reduce((n, [key, value]) => {
      if (key === "search") return n + (String(value || "").trim() ? 1 : 0);
      return n + (value ? 1 : 0);
    }, 0);
  }, [filters]);

  const paged = useMemo(
    () =>
      filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage]
  );

  const formatDt = (v) => (v ? moment(v).format("YYYY-MM-DD HH:mm") : "—");

  const openMenu = (e, row) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const handleSetStatus = async (status) => {
    if (!menuRow) return;
    closeMenu();
    const res = await UpdateDemoRequestStatusCall(menuRow.id, status);
    if (res?.success && res?.data) {
      setRows((prev) =>
        prev.map((r) => (r.id === menuRow.id ? { ...r, ...res.data } : r))
      );
      showToast(t("demoRequests.statusUpdated", "Status updated."));
    } else {
      showToast(
        t("demoRequests.statusError", "Could not update status."),
        "error"
      );
    }
  };

  const openDetails = (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    closeMenu();
  };

  const confirmDelete = (row) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
    closeMenu();
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    const res = await DeleteDemoRequestCall(id);
    setDeleteOpen(false);
    setDeleteTarget(null);
    if (res?.success) {
      setRows((prev) => prev.filter((r) => r.id !== id));
      setDetailOpen(false);
      setDetailRow(null);
      showToast(t("demoRequests.deleted", "Request deleted."));
    } else {
      showToast(
        t("demoRequests.deleteError", "Could not delete."),
        "error"
      );
    }
  };

  const statusLabel = (key) =>
    t(`demoRequests.status.${key}`, STATUS_LABELS_EN[key] || key);

  const renderDetailContent = (row) => {
    if (!row) return null;
    const u = row.user;
    const submittedAt = row.created_at
      ? new Date(row.created_at).toISOString()
      : "—";
    const timePref = labelTime(row.time_preference);
    const pilot = labelPilot(row.pilot);
    const demoDatesPretty = formatDemoDatesPretty(row.demo_dates);
    const uniSnap = row.university;

    const liveUniversity = u?.universityInfo?.name || "";
    const universityForDetail =
      liveUniversity || uniSnap?.name || row.university_name || "—";
    const contactName = requesterName(row) || "—";
    const countryRaw = requesterCountry(row);
    const countryFlag = isoToFlag(countryRaw);
    const countryDisplay = countryRaw
      ? `${countryFlag ? `${countryFlag} ` : ""}${countryRaw}`
      : "—";

    const requestLines = [
      ["Name", contactName],
      ["Request ID", row.id],
      ["Submitted (UTC)", submittedAt],
      ["Updated (UTC)", row.updated_at ? new Date(row.updated_at).toISOString() : "—"],
      ["CRM status", statusLabel(row.status || "new_request")],
      ["Work email", row.work_email],
      ["Phone (form)", row.phone || "—"],
      ["Academic role", row.academic_role],
      ["Teaching area", row.teaching_area],
      ["Class size", row.class_size || "—"],
      ["Main interest", row.main_interest || "—"],
      ["Main goal", row.main_goal || "—"],
      ["Preferred dates (raw)", formatDates(row.demo_dates)],
      ["Preferred dates (formatted)", demoDatesPretty],
      ["Time preference", timePref],
      ["Pilot interest", pilot],
      ["Account role (at submit)", row.account_role || "—"],
      ["Country", countryDisplay],
      ["University", universityForDetail],
      ["University (snapshot at submit)", uniSnap?.name || "—"],
      ["Platform", row.platform || "—"],
      ["App version", row.app_version || "—"],
      ["Source", row.source || "—"],
    ];

    return (
      <Stack spacing={3}>
        <Box>
          <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.detailRequestSection", "Demo request (same fields as admin email)")}
          </MDTypography>
          {requestLines.map(([k, v]) => (
            <DetailLine key={k} label={k} value={v} />
          ))}
        </Box>

        <Box>
          <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.detailAccountSection", "Microlab user profile")}
          </MDTypography>
          {u ? (
            flattenForDisplay(u).map(({ key, value }) => (
              <DetailLine key={key} label={key} value={value} />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t("demoRequests.noUser", "No linked user.")}
            </Typography>
          )}
        </Box>

        {u?.universityInfo ? (
          <Box>
            <MDTypography variant="button" color="info" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
              {t("demoRequests.universitySection", "Linked university")}
            </MDTypography>
            <DetailLine label="Name" value={u.universityInfo.name} />
            <DetailLine label="Status" value={u.universityInfo.status} />
            <DetailLine
              label="License end"
              value={u.universityInfo.license_end ? formatDt(u.universityInfo.license_end) : "—"}
            />
          </Box>
        ) : null}

        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {t(
              "demoRequests.detailFootnote",
              "User object includes linked university (universityInfo) when present."
            )}
          </Typography>
          <MDTypography variant="button" color="text" fontWeight="bold" sx={{ mb: 1, display: "block" }}>
            {t("demoRequests.rawSection", "Raw record (JSON)")}
          </MDTypography>
          <Box
            component="pre"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.100",
              fontSize: "0.72rem",
              overflow: "auto",
              maxHeight: 280,
              m: 0,
            }}
          >
            {JSON.stringify(row, null, 2)}
          </Box>
        </Box>
      </Stack>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        component="main"
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Stack spacing={2.5} sx={{ mb: 3 }}>
          <Box>
            <MDTypography
              variant="h4"
              fontWeight="bold"
              color="dark"
              sx={{ letterSpacing: "-0.02em", color: "#000000 !important" }}
            >
              {t("demoRequests.title", "Customers")}
            </MDTypography>
            <MDTypography
              variant="body1"
              color="dark"
              sx={{ mt: 1, maxWidth: 760, lineHeight: 1.6, color: "#000000 !important" }}
            >
              {t(
                "demoRequests.subtitle",
                "Add customers, send the first email and three reminders from samira@microlab.ink, and see opens and replies on each card."
              )}
            </MDTypography>
            {!loading ? (
              <Typography variant="body2" sx={{ mt: 1, color: "#000000", fontWeight: 600 }}>
                {activeFilterCount
                  ? t("demoRequests.filteredCount", {
                      shown: filteredRows.length,
                      total: rows.length,
                      defaultValue: `Showing ${filteredRows.length} of ${rows.length}`,
                    })
                  : t("demoRequests.totalCount", { count: rows.length })}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1.25} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Box
                component="button"
                type="button"
                onClick={() => setAddOpen(true)}
                sx={{
                  appearance: "none",
                  cursor: "pointer",
                  border: "none",
                  borderRadius: "10px",
                  minHeight: 42,
                  px: 2.5,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  color: "#ffffff",
                  background: "linear-gradient(195deg, #49a3f1, #1A73E8)",
                  boxShadow: "0 4px 10px rgba(26, 115, 232, 0.28)",
                  "&:hover": { filter: "brightness(1.05)" },
                }}
              >
                Add customer
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setTemplatesOpen(true)}
                sx={{
                  appearance: "none",
                  cursor: "pointer",
                  borderRadius: "10px",
                  minHeight: 42,
                  px: 2.5,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  color: "#000000",
                  bgcolor: "background.card",
                  border: "1.5px solid #000000",
                  "&:hover": { bgcolor: "background.elevated" },
                }}
              >
                Email templates
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => checkReplies()}
                sx={{
                  appearance: "none",
                  cursor: "pointer",
                  borderRadius: "10px",
                  minHeight: 42,
                  px: 2.5,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  color: "#000000",
                  bgcolor: "background.card",
                  border: "1.5px solid #000000",
                  "&:hover": { bgcolor: "background.elevated" },
                }}
              >
                Check replies
              </Box>
            </Stack>
            {inboxNote ? (
              <Typography
                variant="body2"
                sx={{ display: "block", mt: 1.25, maxWidth: 720, lineHeight: 1.5, color: "#000000", fontWeight: 500 }}
              >
                {inboxNote}
              </Typography>
            ) : null}
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.card",
            }}
          >
            <Stack spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                value={filters.search}
                onChange={setFilter("search")}
                placeholder="Search name, email, university, phone, notes…"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "background.elevated",
                    minHeight: 44,
                  },
                }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                  gap: 1.25,
                }}
              >
                <FormControl size="small" fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    label="Country"
                    value={filters.country}
                    onChange={setFilter("country")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All countries</MenuItem>
                    {filterOptions.countries.map((value) => (
                      <MenuItem key={value} value={value}>
                        {isoToFlag(value) ? `${isoToFlag(value)} ${value}` : value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>University</InputLabel>
                  <Select
                    label="University"
                    value={filters.university}
                    onChange={setFilter("university")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All universities</MenuItem>
                    {filterOptions.universities.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Class size</InputLabel>
                  <Select
                    label="Class size"
                    value={filters.classSize}
                    onChange={setFilter("classSize")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All class sizes</MenuItem>
                    {filterOptions.classSizes.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={setFilter("status")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All statuses</MenuItem>
                    {statusKeys.map((key) => (
                      <MenuItem key={key} value={key}>
                        {statusLabel(key)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Academic role</InputLabel>
                  <Select
                    label="Academic role"
                    value={filters.academicRole}
                    onChange={setFilter("academicRole")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All roles</MenuItem>
                    {filterOptions.academicRoles.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Teaching area</InputLabel>
                  <Select
                    label="Teaching area"
                    value={filters.teachingArea}
                    onChange={setFilter("teachingArea")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All teaching areas</MenuItem>
                    {filterOptions.teachingAreas.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select
                    label="Source"
                    value={filters.source}
                    onChange={setFilter("source")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All sources</MenuItem>
                    {filterOptions.sources.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value === "manual" ? "Added manually" : value === "form" ? "Demo request" : value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Email answer</InputLabel>
                  <Select
                    label="Email answer"
                    value={filters.answer}
                    onChange={setFilter("answer")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All email states</MenuItem>
                    <MenuItem value="not_sent">No email yet</MenuItem>
                    <MenuItem value="waiting">Sent — waiting</MenuItem>
                    <MenuItem value="opened">Opened</MenuItem>
                    <MenuItem value="replied">Replied</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Pilot interest</InputLabel>
                  <Select
                    label="Pilot interest"
                    value={filters.pilot}
                    onChange={setFilter("pilot")}
                    sx={{ borderRadius: 2, minHeight: 44 }}
                  >
                    <MenuItem value="">All pilot answers</MenuItem>
                    {filterOptions.pilots.map((value) => (
                      <MenuItem key={value} value={value}>
                        {labelPilot(value)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {activeFilterCount ? (
                <Box>
                  <Button
                    size="small"
                    onClick={() => {
                      setFilters(EMPTY_FILTERS);
                      setPage(0);
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, color: "ink.main" }}
                  >
                    Clear filters ({activeFilterCount})
                  </Button>
                </Box>
              ) : null}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.35)"
                  : "0 12px 40px rgba(15, 23, 42, 0.06)",
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={8}>
                <CircularProgress size={36} />
              </Box>
            ) : paged.length === 0 ? (
              <Box py={6} px={2}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {rows.length === 0
                    ? t("demoRequests.empty", "No customers yet. Add one to start the email sequence.")
                    : t(
                        "demoRequests.noFilterMatches",
                        "No customers match these filters. Try clearing search or filters."
                      )}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                  gap: 2,
                  p: 2,
                }}
              >
                {paged.map((row) => (
                  <CustomerCard
                    key={row.id}
                    row={row}
                    statusLabel={statusLabel}
                    statusColor={statusChipColor}
                    flag={isoToFlag(requesterCountry(row))}
                    universityName={universityDisplayName(row)}
                    name={requesterName(row)}
                    onOpen={() => openDetails(row)}
                    onMenu={(e) => openMenu(e, row)}
                    onEmail={() => setEmailRow(row)}
                  />
                ))}
              </Box>
            )}
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t("demoRequests.rowsPerPage", "Rows per page")}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                px: 2,
                py: 1,
              }}
            />
          </Paper>
        </Stack>
      </MDBox>

      <AddCustomerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          fetchRows();
          showToast("Customer added.");
        }}
      />
      <TemplatesDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
      <SendEmailDialog
        open={Boolean(emailRow)}
        row={emailRow}
        onClose={() => setEmailRow(null)}
        onSent={() => {
          fetchRows();
          showToast("Email sent. The count is on the card.");
        }}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 220, borderRadius: 2, mt: 0.5 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ letterSpacing: "0.06em", textTransform: "uppercase" }}
          >
            {t("demoRequests.setStatus", "Set status")}
          </Typography>
        </Box>
        {statusKeys.map((key) => (
          <MenuItem
            key={key}
            dense
            selected={(menuRow?.status || "new_request") === key}
            onClick={() => handleSetStatus(key)}
          >
            {statusLabel(key)}
          </MenuItem>
        ))}
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", my: 0.5 }} />
        <MenuItem onClick={() => menuRow && openDetails(menuRow)}>
          {t("demoRequests.moreInfo", "More info")}
        </MenuItem>
        <MenuItem
          onClick={() => menuRow && confirmDelete(menuRow)}
          sx={{ color: "error.main" }}
        >
          {t("demoRequests.delete", "Delete")}
        </MenuItem>
      </Menu>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDTypography variant="h5" fontWeight="bold">
            {t("demoRequests.detailTitle", "Request & account details")}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {renderDetailContent(detailRow)}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailOpen(false)}>
            {t("common.close", "Close")}
          </Button>
          {detailRow?.id ? (
            <Button color="error" variant="contained" onClick={() => confirmDelete(detailRow)}>
              {t("demoRequests.delete", "Delete")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("demoRequests.deleteTitle", "Delete demo request?")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              "demoRequests.deleteBody",
              "This removes the row permanently. It cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>
            {t("demoRequests.cancel", "Cancel")}
          </Button>
          <Button color="error" variant="contained" onClick={doDelete}>
            {t("demoRequests.delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <MDSnackbar
        color={snackbar.color === "error" ? "error" : "success"}
        icon={snackbar.color === "error" ? "warning" : "check"}
        title={snackbar.color === "error" ? "Error" : "OK"}
        content={snackbar.message}
        dateTime=""
        open={snackbar.show}
        close={() => setSnackbar((s) => ({ ...s, show: false }))}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}
